import { cloneDeep } from 'lodash';
import { lazy, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { INITIAL_DYNAMIC_ATTRIBUTES } from 'hb-builder/components/BuilderComponents/dynamic/Slider/constants';
import SelectSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/SelectSetting';
import AccordionTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/AccordionTitle';
import { useBuilderConfigContext } from 'hb-builder/hooks';
import {
  dataTypeSelectorOptions,
  getContentTypesOptions,
  sanitizeQueryBuild,
} from 'hb-builder/utils/dynamicSelectorsHelpers';
import { useGetDynamicCategories } from 'hb-react/backoffice/requests/dynamicComponents/useGetDynamicCategories';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import { isValueAnOption, nbItemsSliderOptions } from './utils/selectOptions';

const SelectedSetting = {
  discussions: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/DiscussionsContent'
      ),
  ),
  events: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/EventsContent'
      ),
  ),
  news: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/NewsContent'
      ),
  ),
  users: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/UsersContent'
      ),
  ),
  posts: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/PostsContent'
      ),
  ),
  topics: lazy(
    () =>
      import(
        'hb-builder/components/BuilderComponents/dynamic/Slider/SliderSettings/Content/TopicsContent'
      ),
  ),
};

const Content = ({ item, onChange }) => {
  const {
    hivebrite: {
      ids: { topic },
      scope,
    },
    features: {
      dynamic: {
        slider: {
          [scope]: { withLatestPostsOption, withTopPostsOption },
        },
      },
    },
  } = useBuilderConfigContext();
  const { flagBuilderFollowedByUserGroups } = useFeatureFlag();

  const { query, type } = item.attributes?.dynamicAttributes || {};
  const { numberOfItems } = item.attributes?.dynamicAttributes?.query || {};
  const contentTypeIsSet = type !== 'empty';

  const { data: fetchedCategories, isLoading } = useGetDynamicCategories({
    type,
  });

  useEffect(() => {
    if (!contentTypeIsSet) {
      return;
    }

    const isItemsNumberConsistent = isValueAnOption(
      nbItemsSliderOptions[type],
      numberOfItems,
    );

    if (!isItemsNumberConsistent) {
      onChange({
        id: item.id,
        settingKey: 'attributes.dynamicAttributes.query.numberOfItems',
        value: 12,
      });
    }
  }, [contentTypeIsSet, item.id, numberOfItems, onChange, type]);

  useEffect(() => {
    sanitizeQueryBuild({
      onChange,
      id: item.id,
      queryOptions: query,
      contentType: type,
    });
  }, [item.id, onChange, query, type]);

  const categoriesOptions = useMemo(
    () =>
      fetchedCategories?.map(({ name }) => ({
        value: name,
        label: name,
      })),
    [fetchedCategories],
  );

  const dataTypesOptions = useMemo(
    () =>
      dataTypeSelectorOptions({
        includeHighlightedEvents: true,
        includePastEvents: true,
        includeLatestPosts: withLatestPostsOption,
        includeTopPosts: withTopPostsOption,
        includeFollowedByUserGroups: flagBuilderFollowedByUserGroups,
        type,
        networkLevel: scope,
      }),
    [
      flagBuilderFollowedByUserGroups,
      scope,
      type,
      withLatestPostsOption,
      withTopPostsOption,
    ],
  );

  const handleChangeAndReset = ({ id, value }) => {
    onChange({
      id,
      settingKey: 'attributes.dynamicAttributes',
      value: {
        ...cloneDeep(INITIAL_DYNAMIC_ATTRIBUTES({ isTopic: Boolean(topic) })),
        type: value,
      },
    });
  };

  const ContentSettings = SelectedSetting[type];

  const customProps = {
    item,
    onChange,
    handleChangeAndReset,
    contentTypeOptions: getContentTypesOptions({
      showPosts: withLatestPostsOption || withTopPostsOption,
      showUserLane: true,
      showTopics: true,
      showDiscussions: true,
    }),
    nbItemsSliderOptions: nbItemsSliderOptions[type],
    dataTypesOptions,
    categoriesOptions,
    isLoading,
    type,
  };

  return (
    <Wrapper>
      <AccordionTitle isInitiallyOpen title={__('Type of content')}>
        <SelectSetting
          item={item}
          onChange={handleChangeAndReset}
          options={getContentTypesOptions({
            showPosts: withLatestPostsOption || withTopPostsOption,
            showUserLane: true,
            showTopics: true,
            showDiscussions: true,
          })}
          settingKey="dynamicAttributes.type"
          title={s__('Builder|Choose a type of content')}
        />
      </AccordionTitle>
      {contentTypeIsSet && <ContentSettings {...customProps} />}
    </Wrapper>
  );
};

export default Content;

const Wrapper = styled(Box).attrs(() => ({
  flexDirection: 'column',
  width: '100%',
}))``;
